import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  Key as KeyIcon,
  Delete as DeleteIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TravelExplore as TravelExploreIcon,
  Radar as RadarIcon,
  FlashOn as FlashOnIcon,
  Block as BlockIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    siteId: '',
    userId: '',
    action: '',
    status: '',
    startDate: '',
    endDate: '',
  });
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [detailDialog, setDetailDialog] = useState({ open: false, log: null });
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [analyzerLogs, setAnalyzerLogs] = useState([]);
  const [analyzerStats, setAnalyzerStats] = useState(null);
  const [analyzerLoading, setAnalyzerLoading] = useState(false);
  const [analyzerPagination, setAnalyzerPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 0
  });
  const [analyzerDetailDialog, setAnalyzerDetailDialog] = useState({ open: false, log: null });
  const { isAdmin, isManager } = useAuth();

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        ),
      };
      
      const data = await api.audit.list(params);
      setLogs(data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const loadFilterData = useCallback(async () => {
    try {
      const [sitesData, usersData] = await Promise.all([
        api.sites.list(),
        isAdmin ? api.users.list() : Promise.resolve([]),
      ]);
      setSites(sitesData);
      setUsers(usersData);
    } catch (error) {
      // Failed to load filter data
    }
  }, [isAdmin]);

  const loadAnalyzerLogs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setAnalyzerLoading(true);
      const data = await api.audit.analyzerLogs({
        page: analyzerPagination.page,
        limit: analyzerPagination.limit,
      });
      setAnalyzerLogs(data.logs || []);
      setAnalyzerStats(data.stats || null);
      setAnalyzerPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
    } catch (error) {
      // Silently fail - section just won't show data
    } finally {
      setAnalyzerLoading(false);
    }
  }, [isAdmin, analyzerPagination.page, analyzerPagination.limit]);

  const loadStatistics = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
      const data = await api.audit.stats(params);
      setStatistics(data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setStatsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
    loadFilterData();
    loadStatistics();
    loadAnalyzerLogs();
  }, [pagination.page, pagination.limit, loadLogs, loadFilterData, loadStatistics, loadAnalyzerLogs]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadLogs();
    loadStatistics();
  };

  const handleClearFilters = () => {
    setFilters({
      siteId: '',
      userId: '',
      action: '',
      status: '',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => {
      loadLogs();
      loadStatistics();
    }, 100);
  };

  const handleViewDetails = (log) => {
    setDetailDialog({ open: true, log });
  };

  const handleRefresh = () => {
    loadLogs();
    loadStatistics();
    loadAnalyzerLogs();
  };

  const getStatusIcon = (status) => {
    return status === 'success' ? (
      <CheckCircleIcon color="success" fontSize="small" />
    ) : (
      <ErrorIcon color="error" fontSize="small" />
    );
  };

  const getActionIcon = (action) => {
    if (action === 'domain_analysis') return <TravelExploreIcon fontSize="small" />;
    if (action === 'domain_probe') return <RadarIcon fontSize="small" />;
    if (action === 'domain_quick_check') return <FlashOnIcon fontSize="small" />;
    if (action?.includes('url')) return <LinkIcon fontSize="small" />;
    if (action?.includes('surrogate')) return <KeyIcon fontSize="small" />;
    if (action?.includes('all')) return <DeleteIcon fontSize="small" />;
    return <SpeedIcon fontSize="small" />;
  };

  const getActionLabel = (action) => {
    const labels = {
      'domain_analysis': 'Domain Analysis',
      'domain_probe': 'ASN Probe',
      'domain_quick_check': 'Quick Check',
      'purge_url': 'URL Purge',
      'purge_surrogate_key': 'Surrogate Key',
      'purge_all': 'Purge All',
    };
    return labels[action] || action?.replace('purge_', '').replace('_', ' ') || 'Unknown';
  };

  // SQLite datetime('now') stores UTC without a 'Z' suffix.
  // Append 'Z' so JavaScript parses it as UTC, not local time.
  const parseUTCDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const s = dateStr.trim();
    // Already has timezone info (Z, +, -)
    if (/[Zz]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
    // Add 'Z' to treat as UTC, also handle space-separated format from SQLite
    return new Date(s.replace(' ', 'T') + 'Z');
  };

  const formatTimeAgo = (dateStr) => {
    const date = parseUTCDate(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const columns = [
    {
      field: 'created_at',
      headerName: 'Date',
      width: 180,
      renderCell: (params) => parseUTCDate(params.value).toLocaleString(),
    },
    {
      field: 'user_name',
      headerName: 'User',
      width: 150,
      renderCell: (params) => params.value || 'Unknown',
    },
    {
      field: 'site_name',
      headerName: 'Site',
      width: 150,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 160,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {getActionIcon(params.value)}
          <Typography variant="body2">
            {getActionLabel(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'target',
      headerName: 'Target',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'success' ? 'success' : 'error'}
          size="small"
          icon={getStatusIcon(params.value)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleViewDetails(params.row)}
          title="View Details"
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Audit & Stats
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Section */}
      {(isAdmin || isManager) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon />
                Cache Purging Statistics
              </Typography>
              {statsLoading && <CircularProgress size={20} />}
            </Box>
            
            {statistics ? (
              <Grid container spacing={3}>
                {/* Period Summary Cards */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {[
                      { key: 'today', label: 'Today' },
                      { key: 'yesterday', label: 'Yesterday' },
                      { key: 'last7Days', label: 'Last 7 Days' },
                      { key: 'last30Days', label: 'Last 30 Days' },
                    ].map((p) => {
                      const data = statistics.periods?.[p.key] || { total: 0, success: 0 };
                      const rate = data.total ? Math.round((data.success * 10000) / data.total) / 100 : 0;
                      return (
                        <Grid key={p.key} item xs={12} sm={6} md={3}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>{p.label}</Typography>
                            <Typography variant="h5" fontWeight={700}>{data.total}</Typography>
                            <Typography variant="body2" color="text.secondary">Total Ops</Typography>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                              <LinearProgress variant="determinate" value={rate} sx={{ height: 6, borderRadius: 3 }} />
                              <Typography variant="caption"> {rate}%</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
                {/* Overall Efficiency */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="h6" gutterBottom>Overall Efficiency</Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {statistics.efficiency?.success_rate || 0}%
                    </Typography>
                    <Typography variant="body2">
                      {statistics.efficiency?.successful_purges || 0} successful / {statistics.efficiency?.total_operations || 0} total operations
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={statistics.efficiency?.success_rate || 0} 
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    />
                  </Paper>
                </Grid>

                {/* Total Operations */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h6" gutterBottom>Total Operations</Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {statistics.total || 0}
                    </Typography>
                    <Typography variant="body2">
                      Cache purge operations recorded
                    </Typography>
                  </Paper>
                </Grid>

                {/* Status Breakdown */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Status Breakdown</Typography>
                    {statistics.byStatus && statistics.byStatus.length > 0 ? statistics.byStatus.map((stat) => (
                      <Box key={stat.status} sx={{ mb: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {stat.status}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {stat.percentage}% ({stat.count})
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={stat.percentage}
                          color={stat.status === 'success' ? 'success' : 'error'}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    )) : (
                      <Typography variant="body2" color="text.secondary">No data yet.</Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Action Types */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Purge Types</Typography>
                    {statistics.byAction && statistics.byAction.length > 0 ? statistics.byAction.map((stat) => (
                      <Box key={stat.action} sx={{ mb: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {stat.action.replace('purge_', '').replace('_', ' ')}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {stat.percentage}% ({stat.count})
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={stat.percentage}
                          color="secondary"
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    )) : (
                      <Typography variant="body2" color="text.secondary">No data yet.</Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Top Users */}
                {isAdmin && statistics.topUsers && statistics.topUsers.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Most Active Users</Typography>
                      {statistics.topUsers.slice(0, 5).map((user, index) => (
                        <Box key={user.email} sx={{ mb: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">
                              #{index + 1} {user.name}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {user.percentage}% ({user.count})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={user.percentage}
                            color="info"
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}

                {/* Top Sites */}
                {statistics.topSites && statistics.topSites.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Most Purged Sites</Typography>
                      {statistics.topSites.slice(0, 5).map((site, index) => (
                        <Box key={site.domain} sx={{ mb: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">
                              #{index + 1} {site.domain}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {site.percentage}% ({site.count})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={site.percentage}
                            color="warning"
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            ) : (!statsLoading && (
              <Alert severity="info">
                No statistics available yet.
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Domain Analyzer Activity - Admin Only */}
      {isAdmin && (
        <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <TravelExploreIcon color="secondary" />
              <Typography variant="h6">Domain Analyzer Activity</Typography>
              {analyzerStats && (
                <Chip 
                  label={`${analyzerStats.total_analyses || 0} total`} 
                  size="small" 
                  color="secondary" 
                  variant="outlined" 
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Analyzer Stats Summary */}
            {analyzerStats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                    <Typography variant="h5" fontWeight={700}>{analyzerStats.full_analyses || 0}</Typography>
                    <Typography variant="caption">Full Analyses</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Typography variant="h5" fontWeight={700}>{analyzerStats.probes || 0}</Typography>
                    <Typography variant="caption">ASN Probes</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="h5" fontWeight={700}>{analyzerStats.quick_checks || 0}</Typography>
                    <Typography variant="caption">Quick Checks</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>{analyzerStats.unique_users || 0}</Typography>
                    <Typography variant="caption">Unique Users</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="h5" fontWeight={700}>{analyzerStats.successful || 0}</Typography>
                    <Typography variant="caption">Successful</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="h5" fontWeight={700}>{analyzerStats.failed || 0}</Typography>
                    <Typography variant="caption">Failed</Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Analyzer Activity Table */}
            {analyzerLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={30} />
              </Box>
            ) : analyzerLogs.length > 0 ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>When</strong></TableCell>
                        <TableCell><strong>User</strong></TableCell>
                        <TableCell><strong>Action</strong></TableCell>
                        <TableCell><strong>Target</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Details</strong></TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyzerLogs.map((log) => {
                        const data = log.response_data;
                        const blockingCount = data?.blocking_detected || data?.summary?.blocking_detected || 0;
                        return (
                          <TableRow key={log.id} hover>
                            <TableCell>
                              <Tooltip title={parseUTCDate(log.created_at).toLocaleString()}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                  {formatTimeAgo(log.created_at)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PersonIcon fontSize="small" color="action" />
                                <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                  {log.user_name || log.user_email || 'Unknown'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getActionIcon(log.action)}
                                label={getActionLabel(log.action)}
                                size="small"
                                color={log.action === 'domain_analysis' ? 'secondary' : log.action === 'domain_probe' ? 'info' : 'warning'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                {log.target || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={log.status}
                                color={log.status === 'success' ? 'success' : 'error'}
                                size="small"
                              />
                              {blockingCount > 0 && (
                                <Chip
                                  icon={<BlockIcon />}
                                  label={`${blockingCount} blocked`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ ml: 0.5 }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {log.action === 'domain_analysis' || log.action === 'domain_quick_check'
                                  ? `${data?.carrierCount || data?.carriersUsed?.length || '?'} carriers`
                                  : log.action === 'domain_probe'
                                  ? `${data?.country || '?'} - ${data?.uniqueAsns || '?'} ASNs found`
                                  : ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => setAnalyzerDetailDialog({ open: true, log })}
                                title="View Details"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={analyzerPagination.total}
                  page={analyzerPagination.page - 1}
                  onPageChange={(_, newPage) => setAnalyzerPagination(prev => ({ ...prev, page: newPage + 1 }))}
                  rowsPerPage={analyzerPagination.limit}
                  onRowsPerPageChange={(e) => setAnalyzerPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            ) : (
              <Alert severity="info">No domain analyzer activity recorded yet.</Alert>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Site</InputLabel>
                <Select
                  value={filters.siteId}
                  onChange={(e) => handleFilterChange('siteId', e.target.value)}
                  label="Site"
                >
                  <MenuItem value="">All Sites</MenuItem>
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {isAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                    label="User"
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  label="Action"
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="purge_url">URL Purge</MenuItem>
                  <MenuItem value="purge_surrogate_key">Surrogate Key</MenuItem>
                  <MenuItem value="purge_all">Purge All</MenuItem>
                  <MenuItem value="domain_analysis">Domain Analysis</MenuItem>
                  <MenuItem value="domain_probe">ASN Probe</MenuItem>
                  <MenuItem value="domain_quick_check">Quick Check</MenuItem>
                  <MenuItem value="domain_analysis,domain_probe,domain_quick_check">All Analyzer Actions</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  size="small"
                  fullWidth
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  size="small"
                  fullWidth
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={logs}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={pagination.total}
          page={pagination.page - 1}
          pageSize={pagination.limit}
          onPageChange={(newPage) => 
            setPagination(prev => ({ ...prev, page: newPage + 1 }))
          }
          pageSizeOptions={[10, 25, 50, 100]}
          onPageSizeChange={(newPageSize) => 
            setPagination(prev => ({ ...prev, limit: newPageSize, page: 1 }))
          }
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, log: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Audit Log Details
        </DialogTitle>
        <DialogContent>
          {detailDialog.log && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography variant="body2" gutterBottom>
                    {parseUTCDate(detailDialog.log.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">User</Typography>
                  <Typography variant="body2" gutterBottom>
                    {detailDialog.log.user_name} ({detailDialog.log.user_email})
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Site</Typography>
                  <Typography variant="body2" gutterBottom>
                    {detailDialog.log.site_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Action</Typography>
                  <Typography variant="body2" gutterBottom>
                    {detailDialog.log.action}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Target</Typography>
                  <Typography variant="body2" gutterBottom>
                    {detailDialog.log.target || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    label={detailDialog.log.status}
                    color={detailDialog.log.status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">IP Address</Typography>
                  <Typography variant="body2" gutterBottom>
                    {detailDialog.log.ip_address || 'N/A'}
                  </Typography>
                </Grid>
                
                {detailDialog.log.error_message && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="subtitle2">Error Message</Typography>
                      <Typography variant="body2">
                        {detailDialog.log.error_message}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                
                {detailDialog.log.response_data && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Response Data</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100', mt: 1 }}>
                      <pre style={{ margin: 0, fontSize: '0.8rem', overflow: 'auto' }}>
                        {JSON.stringify(detailDialog.log.response_data, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, log: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Analyzer Detail Dialog */}
      <Dialog
        open={analyzerDetailDialog.open}
        onClose={() => setAnalyzerDetailDialog({ open: false, log: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TravelExploreIcon color="secondary" />
          Domain Analyzer Log Details
        </DialogTitle>
        <DialogContent>
          {analyzerDetailDialog.log && (() => {
            const log = analyzerDetailDialog.log;
            const data = log.response_data;
            return (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                    <Typography variant="body2" gutterBottom>
                      {parseUTCDate(log.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">User</Typography>
                    <Typography variant="body2" gutterBottom>
                      {log.user_name} ({log.user_email})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Action Type</Typography>
                    <Chip
                      icon={getActionIcon(log.action)}
                      label={getActionLabel(log.action)}
                      size="small"
                      color={log.action === 'domain_analysis' ? 'secondary' : log.action === 'domain_probe' ? 'info' : 'warning'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip
                      label={log.status}
                      color={log.status === 'success' ? 'success' : 'error'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Target</Typography>
                    <Typography variant="body2" gutterBottom sx={{ wordBreak: 'break-all' }}>
                      {log.target || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                    <Typography variant="body2" gutterBottom>
                      {log.ip_address || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">User Agent</Typography>
                    <Typography variant="body2" noWrap gutterBottom title={log.user_agent}>
                      {log.user_agent || 'N/A'}
                    </Typography>
                  </Grid>

                  {log.error_message && (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="subtitle2">Error</Typography>
                        <Typography variant="body2">{log.error_message}</Typography>
                      </Alert>
                    </Grid>
                  )}
                  
                  {/* Analysis-specific details */}
                  {data && (log.action === 'domain_analysis' || log.action === 'domain_quick_check') && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Analysis Results
                        </Typography>
                      </Grid>
                      {data.summary && (
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={3}>
                              <Paper sx={{ p: 1, textAlign: 'center' }}>
                                <Typography variant="h6">{data.summary.total_tested || 0}</Typography>
                                <Typography variant="caption">Tested</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: data.summary.blocking_detected > 0 ? 'error.light' : 'transparent' }}>
                                <Typography variant="h6" color={data.summary.blocking_detected > 0 ? 'error.contrastText' : 'inherit'}>
                                  {data.summary.blocking_detected || 0}
                                </Typography>
                                <Typography variant="caption" color={data.summary.blocking_detected > 0 ? 'error.contrastText' : 'inherit'}>Blocked</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light' }}>
                                <Typography variant="h6" color="success.contrastText">{data.summary.successful || 0}</Typography>
                                <Typography variant="caption" color="success.contrastText">Accessible</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Paper sx={{ p: 1, textAlign: 'center' }}>
                                <Typography variant="h6">{data.summary.skipped || 0}</Typography>
                                <Typography variant="caption">Skipped</Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {data.carriers && data.carriers.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Carriers Tested</Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {data.carriers.map((c, i) => (
                              <Chip
                                key={i}
                                label={`${c.country} - ${c.name || 'AS' + c.asn}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {data.results && data.results.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Per-Carrier Results</Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Carrier</TableCell>
                                  <TableCell>Country</TableCell>
                                  <TableCell>ASN</TableCell>
                                  <TableCell>Blocked</TableCell>
                                  <TableCell>Verified</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {data.results.map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.carrier || 'N/A'}</TableCell>
                                    <TableCell>{r.country || 'N/A'}</TableCell>
                                    <TableCell>AS{r.asn || '?'}</TableCell>
                                    <TableCell>
                                      {r.skipped ? (
                                        <Chip label="Skipped" size="small" color="default" />
                                      ) : r.blocked ? (
                                        <Chip label={r.blockingType || 'Blocked'} size="small" color="error" />
                                      ) : (
                                        <Chip label="Accessible" size="small" color="success" />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {r.asnVerified ? (
                                        <CheckCircleIcon fontSize="small" color="success" />
                                      ) : (
                                        <ErrorIcon fontSize="small" color="disabled" />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                    </>
                  )}

                  {/* Probe-specific details */}
                  {data && log.action === 'domain_probe' && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Probe Results
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="h6">{data.country || '?'}</Typography>
                          <Typography variant="caption">Country</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="h6">{data.rotations || 0}</Typography>
                          <Typography variant="caption">Rotations</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                          <Typography variant="h6">{data.uniqueAsns || 0}</Typography>
                          <Typography variant="caption">Unique ASNs</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="h6">{data.errors || 0}</Typography>
                          <Typography variant="caption">Errors</Typography>
                        </Paper>
                      </Grid>
                      {data.topAsns && data.topAsns.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Top ASNs Discovered</Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ASN</TableCell>
                                  <TableCell>Name</TableCell>
                                  <TableCell align="right">Hits</TableCell>
                                  <TableCell align="right">%</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {data.topAsns.map((asn, i) => (
                                  <TableRow key={i}>
                                    <TableCell>AS{asn.asn}</TableCell>
                                    <TableCell>{asn.name}</TableCell>
                                    <TableCell align="right">{asn.hits}</TableCell>
                                    <TableCell align="right">{asn.percentage}%</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                    </>
                  )}

                  {/* Raw data fallback */}
                  {data && (
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2" color="text.secondary">Raw Response Data</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                            <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto', maxHeight: 300 }}>
                              {JSON.stringify(data, null, 2)}
                            </pre>
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyzerDetailDialog({ open: false, log: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
